'use client'

import { useSiteConfig } from '@/feature/config/hooks/useSiteConfig.client'
import { useIsActiveHideVatPrice } from '@/feature/product/hooks/useIsActiveHideVatPrice'
import { useProductCardAnalytics } from '@/feature/product/hooks/useProductAnalytics'
import { PRODUCT_IMAGE_SIZE_MEDIUM } from '@/feature/product/services/product.constants'
import { toProductPage } from '@/feature/product/services/product.routing'
import { referencesProductSelectedColumnsAtom } from '@/feature/product/services/referencesProduct.store'
import type { MergedArticle } from '@/feature/product/types/mergedProduct'
import { shouldDisplayProductPrices } from '@/feature/product/utils/productAvailabilities'
import { getProductDisplayPrice } from '@/feature/product/utils/productDisplayPrice'
import { twMerge } from '@/lib/classname/twMerge'
import { useTranslation } from '@/lib/i18n/useTranslation.client'
import { Divider } from '@/ui/layouts/divider/Divider'
import { Link } from '@/ui/links/link/Link.client'
import { CloudflarePictureProduct } from '@/ui/media/cloudflare-picture-product/CloudflarePictureProduct.client'
import { TextSkeleton } from '@/ui/skeleton/TextSkeleton'
import type {
  ProductAvailabilities_PickupPointOfSaleFragment,
  ProductAvailabilities_ProductFragment,
} from '@gql-generated/graphql'
import { useAtomValue } from 'jotai'
import { memo, Suspense, useMemo } from 'react'
import { AddProductToFavoriteButton } from '../../../add-product-to-favorite-button/AddProductToFavoriteButton.client'
import { ProductAvailabilities } from '../../../product-availabilities/ProductAvailabilities.client'
import { ProductAvailabilitiesSkeleton } from '../../../product-availabilities/ProductAvailabilitiesSkeleton'
import { ProductPrice } from '../../../product-price/ProductPrice.client'
import { ProductStoreStockLink } from '../../../product-store-stock-link/ProductStoreStockLink.client'
import { useReferencesProductTableContext } from './ReferencesProductContext'
import { TECHNICAL_SPECS_LIMIT } from './ReferencesProductRow.client'

export type ReferencesProductRowDesktopDetailsProps = {
  article: MergedArticle
  isFavorite?: boolean
  position?: number
  selectedQuantity?: number
  pickupPointOfSale?: ProductAvailabilities_PickupPointOfSaleFragment | null
}

function ReferencesProductRowDesktopDetailsComponent({
  article,
  isFavorite,
  position,
  selectedQuantity,
  pickupPointOfSale,
}: ReferencesProductRowDesktopDetailsProps) {
  const {
    isAuthenticated,
    isArticlesPriceAndStockLoading,
    isMonoDiscriminant,
    isClickAndCollectEnabled,
    isPublicPriceEnabled,
  } = useReferencesProductTableContext()
  const { t } = useTranslation(['referencesProductTable'])

  const columns = useAtomValue(referencesProductSelectedColumnsAtom)

  const { config } = useSiteConfig()
  const isConnectedLocalStockVisible = config?.catalog?.isConnectedLocalStockVisible ?? false
  const isActiveHideVatPrice = useIsActiveHideVatPrice()

  const isProductPriceDisplayable = useMemo(
    () =>
      shouldDisplayProductPrices({
        product: article,
        isPublicPriceEnabled,
        isAuthenticated,
        isProduct: true,
      }),
    [article, isAuthenticated, isPublicPriceEnabled],
  )

  const hasPrice = 'price' in article && article.price !== undefined // The opposite result of PriceAndStock down status
  const hasExclTaxPrice = (article.price?.exclTax || 0) > 0
  const isIncludedTaxPriceDisplayed = hasPrice && !!article.price?.inclTax && !isActiveHideVatPrice

  const source = isMonoDiscriminant
    ? 'fiche produit mono-discriminant - tableau de références'
    : 'fiche produit multi-discriminant'

  const { trackProductPageAnalytics } = useProductCardAnalytics({
    product: article,
    position,
    isAuthenticated,
    source,
  })

  // FIXME: URGENT replace process.env.NEXT_PUBLIC_BRAND !== 'dexis' by some M2 config
  const showStoreStockLink =
    process.env.NEXT_PUBLIC_BRAND !== 'dexis' &&
    isAuthenticated &&
    isConnectedLocalStockVisible &&
    article.canAddToCart

  const displayPrice = useMemo(
    () => getProductDisplayPrice(article, { withErpPrice: true }),
    [article],
  )

  return (
    <>
      <td colSpan={1 + columns.length} className="p-2">
        <div className="flex items-start gap-x-6 gap-y-3">
          <Link
            variant="free"
            href={`${toProductPage(article.urlKey)}?source=referenceTable`}
            onPress={() => trackProductPageAnalytics()}
          >
            <CloudflarePictureProduct
              className={twMerge(
                'max-w-[8.75rem]',
                !article.mainImage?.set?.length && 'w-[8.75rem]',
              )}
              src={article.mainImage?.set?.[0]?.url}
              alt={article.mainImage?.alt || article.name || ''}
              width={PRODUCT_IMAGE_SIZE_MEDIUM}
            />
          </Link>
          <div className="flex flex-col">
            {article.brand && (
              <span className="font-bold uppercase text-brand-2-900">{article.brand.name}</span>
            )}

            <Link
              variant="free"
              className="mt-1 line-clamp-2 leading-light"
              href={`${toProductPage(article.urlKey)}?source=referenceTable`}
              onPress={() => trackProductPageAnalytics()}
            >
              {article.name}
            </Link>

            {!!article.additionalTechnicalSpecs?.length && (
              <ul className="ml-3 mt-2 list-disc">
                {article.additionalTechnicalSpecs
                  .slice(0, TECHNICAL_SPECS_LIMIT)
                  .map(({ label, value }) => (
                    <li key={label}>
                      {t('referencesProductTable:technicalSpec', { label, value })}
                    </li>
                  ))}
              </ul>
            )}
          </div>
        </div>
      </td>
      <td colSpan={3} className="relative p-2">
        {isProductPriceDisplayable && (
          <Suspense fallback={<TextSkeleton className="w-20" size="4xl" leading="normal" />}>
            {isArticlesPriceAndStockLoading ? (
              <TextSkeleton className="w-20" size="4xl" leading="normal" />
            ) : (
              hasPrice &&
              hasExclTaxPrice && (
                <ProductPrice
                  className="mb-1"
                  price={displayPrice.price}
                  priceQuantity={displayPrice.priceQuantity}
                  unitPrice={displayPrice.erpPrice}
                  discount={{
                    price: displayPrice.discountAmount,
                    percentage: displayPrice.discountPercentage,
                    tiersPrices: displayPrice.tiersPrices,
                  }}
                  selectedQuantity={selectedQuantity}
                  packaging={article.stock?.packaging}
                  size="4xl"
                  inclTaxPrice={isIncludedTaxPriceDisplayed ? displayPrice.inclTaxPrice : 0}
                  wrapperClassName={isIncludedTaxPriceDisplayed ? 'items-end' : 'items-baseline'}
                />
              )
            )}
          </Suspense>
        )}

        <div className="flex flex-col gap-0.5">
          <Suspense
            fallback={<ProductAvailabilitiesSkeleton offset={[{ nbOfLine: 1, title: true }]} />}
          >
            {isArticlesPriceAndStockLoading ? (
              <ProductAvailabilitiesSkeleton offset={[{ nbOfLine: 1, title: true }]} />
            ) : (
              hasPrice && (
                <ProductAvailabilities
                  pickupPointOfSale={pickupPointOfSale}
                  product={article as ProductAvailabilities_ProductFragment}
                  isTitleVisible
                  isClickAndCollectEnabled={isClickAndCollectEnabled}
                  dividerClassName="my-0.5"
                  selectedQuantity={selectedQuantity}
                  isClarityDelayEnabled={Boolean(isAuthenticated && selectedQuantity)}
                />
              )
            )}
          </Suspense>
          {showStoreStockLink && (
            <>
              <Divider direction="horizontal" variant="line" className="my-1" />
              <ProductStoreStockLink
                className="self-start"
                productRef={article.reference}
                isReferenceTableLink
              />
            </>
          )}
        </div>

        {isAuthenticated && article.name && (
          <div className="absolute right-2 top-2">
            <AddProductToFavoriteButton
              product={{
                ...article,
                reference: article.reference,
                name: article.name,
                isFavorite,
              }}
              context={isMonoDiscriminant ? 'mono-discriminant' : 'multi-discriminant'}
            />
          </div>
        )}
      </td>
    </>
  )
}

export const ReferencesProductRowDesktopDetails = memo(ReferencesProductRowDesktopDetailsComponent)
